import { Divider, IconButton, MenuItem, Stack, TextField } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { Typography } from "@platform/design-system";

import { DollarAmountsField } from "./DollarAmountsField";
import {
  type AgeUnit,
  type DraftChildBand,
  isValidChildBand,
  MULTIPLE_DOLLAR_AMOUNTS,
  NOT_INCLUDED,
} from "./childAgeBandValues";

import type { ReactElement } from "react";

/** Upper bound on entries for the multiple-dollar-amounts benefit type. */
const MAX_AMOUNTS = 10;

const AGE_UNIT_OPTIONS: { value: AgeUnit; label: string }[] = [
  { value: "days", label: "Days" },
  { value: "months", label: "Months" },
  { value: "years", label: "Years" },
];

/** Single-amount input label per benefit type; absent types hide the input. */
const SINGLE_AMOUNT_LABEL: Record<string, string> = {
  dollarAmount: "Dollar Amount",
  percentageOfEmployeeElection: "Percentage",
  increments: "Increment Amount",
};

export interface BenefitTypeOption {
  value: string;
  label: string;
}

interface ChildAgeBandsFieldProps {
  bands: DraftChildBand[];
  typeOptions: BenefitTypeOption[];
  showErrors: boolean;
  onUpdate: (index: number, patch: Partial<DraftChildBand>) => void;
  onRemove: (index: number) => void;
  onAmountUpdate: (index: number, amountIndex: number, value: string) => void;
  onAmountAdd: (index: number) => void;
  onAmountRemove: (index: number, amountIndex: number) => void;
}

interface AgeBoundProps {
  label: string;
  unit: AgeUnit;
  limit: string;
  invalid: boolean;
  onUnitChange: (unit: AgeUnit) => void;
  onLimitChange: (limit: string) => void;
}

const AgeBoundRow = ({
  label,
  unit,
  limit,
  invalid,
  onUnitChange,
  onLimitChange,
}: AgeBoundProps): ReactElement => (
  <Stack direction="row" spacing={1} alignItems="center">
    <TextField
      select
      size="small"
      label={`${label} Age Type`}
      value={unit}
      onChange={(e) => onUnitChange(e.target.value as AgeUnit)}
      sx={{ minWidth: 120 }}
    >
      {AGE_UNIT_OPTIONS.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>

    <TextField
      size="small"
      type="number"
      label={`${label} Age Limit`}
      value={limit}
      // Age limits are whole numbers only (CD-1976); ignore any non-integer input.
      onChange={(e) => {
        if (/^\d*$/.test(e.target.value)) onLimitChange(e.target.value);
      }}
      error={invalid}
      inputProps={{ min: 0, step: 1 }}
      fullWidth
    />
  </Stack>
);

interface BandAmountProps {
  band: DraftChildBand;
  index: number;
  showErrors: boolean;
  invalid: boolean;
  onUpdate: (index: number, patch: Partial<DraftChildBand>) => void;
  onAmountUpdate: (index: number, amountIndex: number, value: string) => void;
  onAmountAdd: (index: number) => void;
  onAmountRemove: (index: number, amountIndex: number) => void;
}

const BandAmount = ({
  band,
  index,
  showErrors,
  invalid,
  onUpdate,
  onAmountUpdate,
  onAmountAdd,
  onAmountRemove,
}: BandAmountProps): ReactElement | null => {
  if (band.benefitType === NOT_INCLUDED || band.benefitType === "") {
    return null;
  }

  if (band.benefitType === MULTIPLE_DOLLAR_AMOUNTS) {
    return (
      <DollarAmountsField
        entries={band.amounts}
        showErrors={showErrors}
        maxEntries={MAX_AMOUNTS}
        onUpdate={(amountIndex, value) =>
          onAmountUpdate(index, amountIndex, value)
        }
        onAdd={() => onAmountAdd(index)}
        onRemove={(amountIndex) => onAmountRemove(index, amountIndex)}
      />
    );
  }

  return (
    <TextField
      size="small"
      type="number"
      label={SINGLE_AMOUNT_LABEL[band.benefitType] ?? "Amount"}
      value={band.amount}
      onChange={(e) => onUpdate(index, { amount: e.target.value })}
      error={invalid}
      inputProps={{ min: 0 }}
      fullWidth
    />
  );
};

/**
 * Controlled list of child benefit-description age bands. Each band carries its
 * own lower/upper age-type (unit) selectors and limits, plus a benefit type and
 * amount. Rendered by both surfaces' child age-band editors.
 */
export const ChildAgeBandsField = ({
  bands,
  typeOptions,
  showErrors,
  onUpdate,
  onRemove,
  onAmountUpdate,
  onAmountAdd,
  onAmountRemove,
}: ChildAgeBandsFieldProps): ReactElement => (
  <>
    {bands.map((band, index) => {
      const invalid = showErrors && !isValidChildBand(band);

      return (
        <Stack key={band.id} spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body-medium">
              <strong>Age Band {index + 1}</strong>
            </Typography>

            <IconButton
              size="small"
              aria-label="Remove age band"
              onClick={() => onRemove(index)}
            >
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>

          <AgeBoundRow
            label="Lower"
            unit={band.lowerUnit}
            limit={band.lowerLimit}
            invalid={invalid}
            onUnitChange={(lowerUnit) => onUpdate(index, { lowerUnit })}
            onLimitChange={(lowerLimit) => onUpdate(index, { lowerLimit })}
          />

          <AgeBoundRow
            label="Upper"
            unit={band.upperUnit}
            limit={band.upperLimit}
            invalid={invalid}
            onUnitChange={(upperUnit) => onUpdate(index, { upperUnit })}
            onLimitChange={(upperLimit) => onUpdate(index, { upperLimit })}
          />

          <TextField
            select
            size="small"
            label="Benefit Type"
            value={band.benefitType}
            onChange={(e) => onUpdate(index, { benefitType: e.target.value })}
            error={invalid}
            fullWidth
          >
            {typeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <BandAmount
            band={band}
            index={index}
            showErrors={showErrors}
            invalid={invalid}
            onUpdate={onUpdate}
            onAmountUpdate={onAmountUpdate}
            onAmountAdd={onAmountAdd}
            onAmountRemove={onAmountRemove}
          />

          {index < bands.length - 1 && <Divider />}
        </Stack>
      );
    })}
  </>
);
